import { AccountRole } from "@common/types/domains/accounting/account";
import { AccountType } from "./accounts";

export const ACCOUNT_ROLE_COPY = {
    [AccountRole.uncategorizedIncome]: {
        label: "Uncategorized income account",
        description: "Money in that has not been categorized yet posts here.",
    },
    [AccountRole.uncategorizedExpense]: {
        label: "Uncategorized expense account",
        description: "Money out that has not been categorized yet posts here.",
    },
    [AccountRole.accountsReceivable]: {
        label: "Receivables account",
        description: "Customer invoice balances post here by default.",
    },
    [AccountRole.accountsPayable]: {
        label: "Payables account",
        description: "Vendor bill balances post here by default.",
    },
    [AccountRole.defaultRevenue]: {
        label: "Default revenue account",
        description:
            "Invoice line items and income transactions post here by default.",
    },
    [AccountRole.badDebtExpense]: {
        label: "Bad debt expense account",
        description:
            "When a customer balance is written off as uncollectible, the expense posts here.",
    },
    [AccountRole.cashOnHand]: {
        label: "Cash on hand account",
        description:
            "Manually recorded cash payments on bills and invoices post here.",
    },
    [AccountRole.bankTransferClearing]: {
        label: "Bank transfer clearing account",
        description:
            "When a transfer is matched between two bank accounts, it clears through this account so it is not counted as income or expense.",
    },
    [AccountRole.paymentTransferClearing]: {
        label: "Payment transfer clearing account",
        description:
            "Only used with payment processors like Stripe or PayPal. Transfers matched to or from a processor clear through this account so they are not counted as income or expense.",
    },
    [AccountRole.creditCardClearing]: {
        label: "Credit card clearing account",
        description:
            "Credit card payments clear through this account until they are matched to a bank withdrawal.",
    },
    [AccountRole.payrollClearing]: {
        label: "Payroll clearing account",
        description:
            "Only used with payroll providers like Gusto. Payroll clears through this account until the bank withdrawal is matched.",
    },
    [AccountRole.reimbursementsClearing]: {
        label: "Reimbursements clearing account",
        description:
            "Only used with reimbursement tools like Ramp. Transfers matched on a reimbursement account clear through this account so they are not counted as income or expense.",
    },
    [AccountRole.salesTaxPayable]: {
        label: "Sales tax payable account",
        description: "Sales tax collected on invoices posts here.",
    },
    [AccountRole.customerDeposits]: {
        label: "Customer deposits account",
        description:
            "Customer deposits and prepayments post here when applied on an invoice.",
    },
    [AccountRole.discountsAndAllowances]: {
        label: "Discounts and allowances account",
        description: "Discounts and allowances on invoices post here.",
    },
    [AccountRole.prepaidExpenses]: {
        label: "Prepaid expenses account",
        description: "Deposits paid on bills post here.",
    },
    [AccountRole.retainedEarningsOrOwnersCapital]: {
        label: "Retained earnings / owner's capital account",
        description: "Accumulated earnings on the Balance Sheet post here.",
    },
    [AccountRole.ownerContributions]: {
        label: "Owner contributions account",
        description:
            "Activity moved between personal and business posts here, as do incoming transfers matched to personal.",
    },
    [AccountRole.ownerDraws]: {
        label: "Owner draws account",
        description:
            "Activity moved between business and personal posts here, as do outgoing transfers matched to personal.",
    },
    [AccountRole.intercompanyReceivable]: {
        label: "Intercompany receivable account",
        description:
            "Only used with more than one entity. Amounts owed from related businesses post here when there is no Due to/from account for that business.",
    },
    [AccountRole.intercompanyPayable]: {
        label: "Intercompany payable account",
        description:
            "Only used with more than one entity. Amounts owed to related businesses post here when there is no Due to/from account for that business.",
    },
} as const satisfies Record<
    AccountRole,
    { label: string; description: string }
>;

export const ACCOUNT_ROLE_ELIGIBLE_TYPES = {
    [AccountRole.uncategorizedIncome]: [
        AccountType.income,
        AccountType.otherIncome,
    ],
    [AccountRole.uncategorizedExpense]: [
        AccountType.operatingExpenses,
        AccountType.otherExpenses,
    ],
    [AccountRole.accountsReceivable]: [AccountType.accountsReceivable],
    [AccountRole.accountsPayable]: [AccountType.accountsPayable],
    [AccountRole.defaultRevenue]: [AccountType.income],
    [AccountRole.badDebtExpense]: [
        AccountType.operatingExpenses,
        AccountType.otherExpenses,
    ],
    [AccountRole.cashOnHand]: [AccountType.cash],
    [AccountRole.bankTransferClearing]: [
        AccountType.cash,
        AccountType.otherCurrentAssets,
    ],
    [AccountRole.paymentTransferClearing]: [
        AccountType.cash,
        AccountType.otherCurrentAssets,
    ],
    [AccountRole.creditCardClearing]: [
        AccountType.creditCards,
        AccountType.otherCurrentLiabilities,
    ],
    [AccountRole.payrollClearing]: [AccountType.operatingExpenses],
    [AccountRole.reimbursementsClearing]: [AccountType.otherCurrentLiabilities],
    [AccountRole.salesTaxPayable]: [AccountType.otherCurrentLiabilities],
    [AccountRole.customerDeposits]: [AccountType.otherCurrentLiabilities],
    [AccountRole.discountsAndAllowances]: [AccountType.income],
    [AccountRole.prepaidExpenses]: [AccountType.otherCurrentAssets],
    [AccountRole.retainedEarningsOrOwnersCapital]: [AccountType.equity],
    [AccountRole.ownerContributions]: [AccountType.equity],
    [AccountRole.ownerDraws]: [AccountType.equity],
    [AccountRole.intercompanyReceivable]: [
        AccountType.accountsReceivable,
        AccountType.otherCurrentAssets,
        AccountType.otherAssets,
    ],
    [AccountRole.intercompanyPayable]: [
        AccountType.accountsPayable,
        AccountType.otherCurrentLiabilities,
        AccountType.otherLiabilities,
    ],
} as const satisfies Record<AccountRole, readonly AccountType[]>;

export function getAccountRoleReassignmentBlockMessage(
    role: AccountRole | null | undefined,
): string | null {
    if (role === AccountRole.accountsReceivable) {
        return "The Receivables account role can't be reassigned. To use a different account, merge this account into it.";
    }
    if (role === AccountRole.accountsPayable) {
        return "The Payables account role can't be reassigned. To use a different account, merge this account into it.";
    }
    return null;
}

const ACCOUNT_ROLES: ReadonlySet<string> = new Set(Object.values(AccountRole));

export function isAccountRole(value: string): value is AccountRole {
    return ACCOUNT_ROLES.has(value);
}

export function formatEligibleTypesForRole(role: AccountRole): string {
    return ACCOUNT_ROLE_ELIGIBLE_TYPES[role].join(" or ");
}

export function isAccountTypeEligibleForRole(
    role: AccountRole,
    type: AccountType,
): boolean {
    return ACCOUNT_ROLE_ELIGIBLE_TYPES[role].some(
        (eligibleType) => eligibleType === type,
    );
}
